import { reportSentryError } from './reportSentryError';

export type ApiFailureKind =
  | 'api_5xx'
  | 'service_unavailable'
  | 'network_timeout'
  | 'network_failure'
  | 'client_offline';

export interface ApiFailureContext {
  requestUrl?: string;
  method?: string;
  status?: number;
  businessCode?: number;
  responseMessage?: unknown;
  traceId?: string;
  durationMs?: number;
  page?: string;
  renderMode?: 'ssr' | 'csr';
  isOnline?: boolean;
}

type ApiErrorLike = {
  code?: unknown;
  config?: { method?: unknown; url?: unknown };
  message?: unknown;
  name?: unknown;
  request?: unknown;
  response?: {
    data?: unknown;
    headers?: unknown;
    status?: unknown;
  };
};

const SERVICE_UNAVAILABLE_STATUSES = new Set([502, 503, 504]);
const TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);
const NETWORK_CODES = new Set([
  'ECONNREFUSED',
  'ECONNRESET',
  'ENETUNREACH',
  'ENOTFOUND',
  'ERR_NETWORK',
]);
const CANCELED_CODES = new Set(['ERR_CANCELED']);
const MAX_MESSAGE_LENGTH = 500;

function asApiError(error: unknown): ApiErrorLike {
  return error && typeof error === 'object' ? (error as ApiErrorLike) : {};
}

function asNumber(value: unknown) {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

function asString(value: unknown) {
  return typeof value === 'string' && value ? value : undefined;
}

function getSafePath(url: unknown) {
  if (typeof url !== 'string' || !url) return '';

  const urlWithoutQuery = url.split(/[?#]/, 1)[0] || '';
  try {
    return new URL(urlWithoutQuery, 'https://zeelool.invalid').pathname;
  } catch {
    return urlWithoutQuery;
  }
}

function getNormalizedRoute(path: string) {
  return path
    .split('/')
    .map((segment) => {
      if (/^\d+$/.test(segment)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment)) return ':id';
      if (/^[a-z0-9_-]{16,}$/i.test(segment) && /\d/.test(segment)) return ':id';
      return segment;
    })
    .join('/');
}

function sanitizeMessage(value: unknown) {
  const message = asString(value);
  if (!message) return undefined;

  return message
    .slice(0, MAX_MESSAGE_LENGTH)
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]')
    .replace(/\b(?:bearer)\s+[a-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
    .replace(
      /\b(token|secret|password|passwd|pwd|authorization)\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;]+)/gi,
      '$1=[REDACTED]',
    )
    .replace(/\b(?:\+?\d[\d\s().-]{7,}\d)\b/g, '[REDACTED_PHONE]');
}

function getResponseMessage(data: unknown) {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return undefined;

  const responseData = data as Record<string, unknown>;
  return responseData.message ?? responseData.msg ?? responseData.error;
}

function getHeader(headers: unknown, headerNames: string[]) {
  if (!headers || typeof headers !== 'object') return undefined;

  const headerContainer = headers as {
    get?: (name: string) => unknown;
    [key: string]: unknown;
  };
  for (const headerName of headerNames) {
    const getValue = headerContainer.get?.(headerName);
    if (typeof getValue === 'string' && getValue) return getValue;

    const matched = Object.entries(headerContainer).find(
      ([key]) => key.toLowerCase() === headerName.toLowerCase(),
    )?.[1];
    if (typeof matched === 'string' && matched) return matched;
  }
}

function classifyFailure(
  error: ApiErrorLike,
  status: number | undefined,
  businessCode: number | undefined,
  isOnline: boolean | undefined,
): ApiFailureKind | null {
  const code = asString(error.code)?.toUpperCase() || '';
  const name = asString(error.name) || '';
  const message = asString(error.message) || '';

  if (CANCELED_CODES.has(code) || name === 'CanceledError' || name === 'AbortError') return null;
  if (SERVICE_UNAVAILABLE_STATUSES.has(status || 0)) return 'service_unavailable';
  if ((status || 0) >= 500 || (businessCode || 0) >= 500) return 'api_5xx';
  if ((status || 0) >= 400) return null;
  if (TIMEOUT_CODES.has(code) || name === 'TimeoutError' || /timeout/i.test(message)) {
    return 'network_timeout';
  }
  if (isOnline === false) return 'client_offline';
  if (
    NETWORK_CODES.has(code) ||
    (!status && Boolean(error.request)) ||
    (!status && name === 'TypeError' && /fetch|network|request/i.test(message))
  ) {
    return 'network_failure';
  }
  return null;
}

/**
 * 上报会导致页面降级的 API 依赖故障。该函数只读取错误信息并同步投递 Sentry，
 * 自身失败会被吞掉，不能改变请求 Promise、页面跳转或 SSR/CSR 渲染结果。
 */
export function reportApiFailure(error: unknown, context: ApiFailureContext = {}) {
  try {
    const apiError = asApiError(error);
    const status = context.status ?? asNumber(apiError.response?.status);
    const responseData = apiError.response?.data;
    const responseRecord =
      responseData && typeof responseData === 'object'
        ? (responseData as Record<string, unknown>)
        : undefined;
    const businessCode =
      context.businessCode ?? asNumber(responseRecord?.code ?? responseRecord?.status_code);
    const kind = classifyFailure(apiError, status, businessCode, context.isOnline);
    if (!kind) return;

    const requestUrl = context.requestUrl ?? apiError.config?.url;
    const path = getSafePath(requestUrl);
    const route = getNormalizedRoute(path);
    const method = (context.method ?? asString(apiError.config?.method) ?? 'GET').toUpperCase();
    const message = sanitizeMessage(
      context.responseMessage ?? getResponseMessage(responseData) ?? apiError.message,
    );
    const traceId =
      context.traceId ??
      getHeader(apiError.response?.headers, ['x-trace-id', 'trace-id', 'x-request-id']);
    const page = getSafePath(context.page);
    const errorCode = asString(apiError.code);

    reportSentryError(error, {
      domain: 'api_dependency',
      stage: 'api_request',
      tags: {
        'api.biz_code': businessCode,
        'api.method': method,
        'api.route': route || 'unknown',
        'error.kind': kind,
        'error.page': page || undefined,
        'error.source': 'api_dependency',
        'http.status_code': status,
        monitoring_priority: 'api_failure',
        'render.mode': context.renderMode,
      },
      extra: {
        api_message: message,
        api_path: path || undefined,
        duration_ms: context.durationMs,
        error_code: errorCode,
        trace_id: traceId,
      },
      fingerprint: [
        'api_failure',
        kind,
        method,
        route || 'unknown',
        String(status ?? businessCode ?? 'no_status'),
      ],
    });
  } catch {
    // 监控必须完全旁路，任何 SDK/数据异常都不能影响业务请求。
  }
}
